import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { StatisticsController } from 'api/controllers/StatisticsController';
import { OverallStatisticsResponse, CategoryBreakdown } from 'api/controllers/typings';
import { useUserData } from 'providers/user';
import type { UserDataContextType } from 'providers/user/typings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import CategoryIcon from '@mui/icons-material/Category';

export const OverallStatisticsPage = () => {
  const [statistics, setStatistics] = useState<OverallStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const { getUserByEmail } = useUserData() as UserDataContextType;

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const data = await StatisticsController.getOverallStatistics();
        setStatistics(data);
      } catch (err) {
        setError('Failed to load statistics');
        console.error('Error fetching overall statistics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <Container sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity='error'>{error}</Alert>
      </Container>
    );
  }

  if (!statistics) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity='info'>No statistics available</Alert>
      </Container>
    );
  }

  const statsCards = [
    {
      title: 'Total Nominations',
      value: statistics.total_nominations,
      icon: TrendingUpIcon,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      iconColor: '#fff',
      size: 'normal' as const,
    },
    {
      title: 'Nominated People',
      value: statistics.total_nominated_people,
      icon: PeopleIcon,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      iconColor: '#fff',
      size: 'normal' as const,
    },
    {
      title: 'Votes Cast',
      value: statistics.total_votes_cast,
      icon: HowToVoteIcon,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      iconColor: '#fff',
      size: 'normal' as const,
    },
    {
      title: 'Categories',
      value: statistics.category_breakdowns.length,
      icon: CategoryIcon,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      iconColor: '#fff',
      size: 'normal' as const,
    },
  ];

  return (
    <Container sx={{ py: 4 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: stat.gradient,
                  borderRadius: 3,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                }}>
                <CardContent sx={{ p: 3, color: 'white' }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                    <IconComponent sx={{ fontSize: 48, color: stat.iconColor, opacity: 0.9 }} />
                  </Box>
                  <Typography variant='h3' sx={{ fontWeight: 'bold', mb: 1, fontSize: '2.5rem' }}>
                    {stat.value.toLocaleString()}
                  </Typography>
                  <Typography variant='body1' sx={{ opacity: 0.9, fontSize: '1rem', fontWeight: 500 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Category Breakdowns */}
      <Box sx={{ mb: 6 }}>
        <Typography variant='h4' gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.text.primary, mb: 3 }}>
          Category Breakdowns
        </Typography>
        <Grid container spacing={3}>
          {statistics.category_breakdowns.map((category: CategoryBreakdown, index: number) => {
            const gradients = [
              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            ];

            return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    background: gradients[index % gradients.length],
                    borderRadius: 3,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 15px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}>
                  <CardContent sx={{ p: 3, color: 'white', textAlign: 'center' }}>
                    <Typography variant='h4' sx={{ fontWeight: 'bold', mb: 1 }}>
                      {category.count}
                    </Typography>
                    <Typography variant='body1' sx={{ opacity: 0.9, fontWeight: 500 }}>
                      {category.category.replace('_', ' ').toUpperCase()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Top Nominators */}
      <Box sx={{ mb: 6 }}>
        <Typography variant='h4' gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.text.primary, mb: 3 }}>
          Top Nominators
        </Typography>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
          }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}>Email</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}>
                    Nominations
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.top_nominators.map((nominator, index: number) => {
                  const user = getUserByEmail(nominator.nominator_email);
                  const displayName = user ? `${user.first_name} ${user.last_name}` : 'Unknown';

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: alpha(theme.palette.action.hover, 0.02),
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'scale(1.01)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}>
                      <TableCell sx={{ fontSize: '1rem', py: 2, fontWeight: 'bold' }}>{displayName}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem', py: 2, opacity: 0.7 }}>
                        {nominator.nominator_email}
                      </TableCell>
                      <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                        <Chip
                          label={nominator.number_of_nominations}
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            height: '40px',
                            minWidth: '60px',
                            '& .MuiChip-label': {
                              fontSize: '1.2rem',
                              fontWeight: 'bold',
                              px: 2,
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Top Nominees */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h4' gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.text.primary, mb: 3 }}>
          Top Nominees
        </Typography>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
            overflow: 'hidden',
          }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}>Email</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}>
                    Nominations Received
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.top_nominees.map((nominee, index: number) => {
                  const user = getUserByEmail(nominee.nominee_email);
                  const displayName = user ? `${user.first_name} ${user.last_name}` : 'Unknown';

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: alpha(theme.palette.action.hover, 0.02),
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.success.main, 0.08),
                          transform: 'scale(1.01)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}>
                      <TableCell sx={{ fontSize: '1rem', py: 2, fontWeight: 'bold' }}>{displayName}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem', py: 2, opacity: 0.7 }}>{nominee.nominee_email}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                        <Chip
                          label={nominee.number_of_nominations}
                          sx={{
                            backgroundColor: theme.palette.success.main,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            height: '40px',
                            minWidth: '60px',
                            '& .MuiChip-label': {
                              fontSize: '1.2rem',
                              fontWeight: 'bold',
                              px: 2,
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};
